import { FiGrid, FiHelpCircle, FiSettings } from 'react-icons/fi'

import Panel from '~/components/Panel'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import {
  GuideIntro,
  GuideList,
  GuideType,
  GuideTypeCopy,
  GuideTypeGrid,
  GuideTypeIcon,
  GuideTypeTitle
} from './styles'

/**
 * Purely informational panel, exclusive to the Meta channel (FR-024a to
 * FR-024c). Layer 1 of 3 from spec.md "Acesso à conta Meta" — no field to
 * fill, nothing persisted, no lifecycle change. Layers 2 (capturing the ad
 * account id) and 3 (automated access + a waiting state) are deliberately
 * out of MVR1 scope; see the note there before adding either.
 *
 * Layout matches the prototype (ads-design-prototypes, apps/offsite/v2.html,
 * #block-meta-account): the nested guide panel opens into two icon+title+
 * bullet-list columns (Ads Manager / Business Settings), not a single
 * merged paragraph. The outer block is `isCollapsible` (chevron in the
 * header, matching the other `cc-block`s in the same prototype flow --
 * Channel, Campaign type -- which all toggle the same way); it was missing
 * here even though every sibling block in this flow has it.
 */
const MetaAccountAccessInfo: React.FC = () => {
  const { t } = useAPPTranslation()

  const adsManagerSteps = t(
    'rm:campaign.create.MetaAccountAccessInfo.guide.adsManager.steps',
    { returnObjects: true }
  ) as string[]

  const businessSettingsSteps = t(
    'rm:campaign.create.MetaAccountAccessInfo.guide.businessSettings.steps',
    { returnObjects: true }
  ) as string[]

  return (
    <Panel
      title={t('rm:campaign.create.MetaAccountAccessInfo.title')}
      description={t('rm:campaign.create.MetaAccountAccessInfo.subtitle')}
      isCollapsible
      initialCollapsibleState={false}
    >
      <p>{t('rm:campaign.create.MetaAccountAccessInfo.body')}</p>

      <Panel
        title={t('rm:campaign.create.MetaAccountAccessInfo.guide.title')}
        iconLeft={<FiHelpCircle aria-hidden />}
        isCollapsible
        initialCollapsibleState={false}
      >
        <GuideIntro>
          {t('rm:campaign.create.MetaAccountAccessInfo.guide.intro')}
        </GuideIntro>

        <GuideTypeGrid>
          <GuideType>
            <GuideTypeIcon aria-hidden>
              <FiGrid />
            </GuideTypeIcon>
            <GuideTypeCopy>
              <GuideTypeTitle>
                {t(
                  'rm:campaign.create.MetaAccountAccessInfo.guide.adsManager.title'
                )}
              </GuideTypeTitle>
              <GuideList>
                {adsManagerSteps?.map(step => (
                  <li key={step}>{step}</li>
                ))}
              </GuideList>
            </GuideTypeCopy>
          </GuideType>

          <GuideType>
            <GuideTypeIcon aria-hidden>
              <FiSettings />
            </GuideTypeIcon>
            <GuideTypeCopy>
              <GuideTypeTitle>
                {t(
                  'rm:campaign.create.MetaAccountAccessInfo.guide.businessSettings.title'
                )}
              </GuideTypeTitle>
              <GuideList>
                {businessSettingsSteps?.map(step => (
                  <li key={step}>{step}</li>
                ))}
              </GuideList>
            </GuideTypeCopy>
          </GuideType>
        </GuideTypeGrid>
      </Panel>
    </Panel>
  )
}

export default MetaAccountAccessInfo
